import {
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Box,
    Divider,
    CircularProgress,
    ListSubheader,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { Link, useLocation, useMatch } from 'react-router-dom';
import { globalTabsConfig, GlobalTabConfig, tripTabsConfig } from 'src/routes';
import { usePermissions } from 'src/hooks/usePermissions';
import { useViewOnlySecret } from 'src/hooks/useViewOnlySecret';
import { useMemo } from 'react';
import { useFilteredEventTabs } from 'src/hooks/useFilteredEventTabs';
import { useEventHasMap } from 'src/hooks/useEventHasMap';
import { mapTodoDisabledNavSx } from 'src/theme/mapTodoDisabledNavSx';

interface Props {
    open: boolean;
    onClose: () => void;
}

const DRAWER_WIDTH = 240;

const listItemIconSx = {
    minWidth: 'auto',
    mr: 2,
    '& .MuiSvgIcon-root': {
        fontSize: '1.75rem',
    },
};

const listItemTextSx = {
    fontSize: '1.1rem',
    fontWeight: 500,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
};

const inventorySubRoutes: Array<{ id: string; route: string; label: string }> = [
    { id: 'inventory-overview', route: '/inventory/items', label: 'Overview' },
    { id: 'inventory-categories', route: '/inventory/categories', label: 'Categories' },
    { id: 'inventory-storage', route: '/inventory/storage', label: 'Storage locations' },
    { id: 'inventory-events', route: '/inventory/events', label: 'Active events' },
];

const logisticsSubRoutes: Array<{ id: string; route: string; label: string }> = [
    { id: 'logistics-operations', route: '/logistics/operations', label: 'Operations' },
    { id: 'logistics-trips', route: '/logistics/trips', label: 'Trips' },
];

const operationSubRoutes: Array<{ id: string; route: string; label: string }> = [
    { id: 'operation-tasks', route: '/tasks', label: 'Tasks' },
    { id: 'operation-confirmation', route: '/confirmation', label: 'Final Confirmation' },
    { id: 'operation-supply', route: '/supply', label: 'Supply flow' },
];

export default function UnifiedLeftNavigation(props: Props) {
    const { open, onClose } = props;
    const theme = useTheme();
    const location = useLocation();
    const pathname = location.pathname;

    const matchLogisticsOperations = useMatch({ path: '/logistics/operations', end: true });
    const matchLogisticsTrips = useMatch({ path: '/logistics/trips', end: true });
    const matchLogisticsTripDetail = useMatch({ path: '/logistics/trip/:tripId/:tab', end: true });
    const matchLogisticsTripDetailNoTab = useMatch({ path: '/logistics/trip/:tripId', end: true });
    const matchLogisticsOperation = useMatch({ path: '/logistics/operation/:operationId', end: false });
    const matchLogisticsOperationTasks = useMatch({ path: '/logistics/operation/:operationId/tasks', end: true });
    const matchLogisticsOperationConfirmation = useMatch({ path: '/logistics/operation/:operationId/confirmation', end: true });
    const matchLogisticsOperationSupply = useMatch({ path: '/logistics/operation/:operationId/supply', end: true });
    const tripIdFromRoute = matchLogisticsTripDetail?.params?.tripId ?? matchLogisticsTripDetailNoTab?.params?.tripId;
    const tripTabId = matchLogisticsTripDetail?.params?.tab ?? 'overview';
    const operationIdFromRoute = matchLogisticsOperation?.params?.operationId;
    const matchInventoryItems = useMatch({ path: '/inventory/items', end: true });
    const matchInventoryCategories = useMatch({ path: '/inventory/categories', end: true });
    const matchInventoryStorage = useMatch({ path: '/inventory/storage', end: true });
    const matchInventoryEvents = useMatch({ path: '/inventory/events', end: true });

    const eventMatch = useMatch('/event/:eventId/:activeTab');
    const viewOnlyEventMatch = useMatch('/viewevent/:eventId/:activeTab');
    const onEventRoute = eventMatch ?? viewOnlyEventMatch;
    const eventId = onEventRoute?.params?.eventId;
    const activeTabId = onEventRoute?.params?.activeTab;
    const { viewOnly, secret } = useViewOnlySecret();
    const viewOnlyWithSecret = viewOnly && secret != null;
    const routePrefix = viewOnly ? '/viewevent' : '/event';

    const { isLoading, isAdmin, canViewEvents, canViewInventory, canEditUsers } = usePermissions();

    const filteredGlobalTabs = useMemo(() => {
        if (isLoading) return [];
        return globalTabsConfig.filter((tab: GlobalTabConfig) => {
            switch (tab.id) {
                case 'list':
                    return canViewEvents;
                case 'info':
                    return canViewEvents;
                case 'inventory':
                case 'logistics':
                    return canViewInventory;
                case 'docs':
                    return true;
                case 'notifications':
                    return canViewEvents;
                case 'settings':
                    return isAdmin;
                case 'users':
                    return canEditUsers;
                default:
                    return false;
            }
        });
    }, [isLoading, isAdmin, canViewEvents, canViewInventory, canEditUsers]);

    const filteredEventTabs = useFilteredEventTabs(eventId);
    const { hasMap, isLoading: eventMapsLoading } = useEventHasMap(eventId);
    const mapTodoDisabled = !eventMapsLoading && !hasMap;

    const renderGlobalItem = (tab: GlobalTabConfig) => {
        const isList = tab.id === 'list';
        const label = isList ? 'Event List' : tab.label;
        const isExternal = Boolean(tab.externalHref);
        const isLogisticsTab = tab.id === 'logistics';
        const isInventoryTab = tab.id === 'inventory';
        const selected =
            !isExternal &&
            (isLogisticsTab || isInventoryTab
                ? false
                : pathname === tab.route || (tab.route !== '/' && pathname.startsWith(tab.route + '/')));
        const listItemContent = (
            <>
                <ListItemIcon sx={listItemIconSx}>
                    <tab.icon />
                </ListItemIcon>
                <ListItemText primary={label} primaryTypographyProps={{ sx: listItemTextSx }} />
            </>
        );
        return (
            <ListItemButton
                key={tab.id}
                component={isExternal ? 'a' : Link}
                {...(isExternal ? { href: tab.externalHref } : { to: tab.route })}
                selected={selected}
                onClick={onClose}
                sx={{
                    py: 2,
                    '&.Mui-selected': {
                        backgroundColor: 'primary.light',
                        '&:hover': { backgroundColor: 'primary.light' },
                    },
                }}
            >
                {listItemContent}
            </ListItemButton>
        );
    };

    const renderEventSubSection = (preserveSearch = false) => {
        if (!eventId || filteredEventTabs.length === 0) return null;
        return (
            <>
                <ListSubheader sx={{ bgcolor: 'transparent', lineHeight: 2 }}>This event</ListSubheader>
                {filteredEventTabs.map(({ tab }) => {
                    const to = preserveSearch
                        ? { pathname: `${routePrefix}/${eventId}/${tab.id}`, search: location.search }
                        : `${routePrefix}/${eventId}/${tab.id}`;
                    const selected = activeTabId === tab.id;
                    const disabled = mapTodoDisabled && (tab.id === 'map' || tab.id === 'todo');
                    return (
                        <ListItemButton
                            key={tab.id}
                            disabled={disabled}
                            {...(disabled ? {} : { component: Link, to })}
                            selected={selected}
                            onClick={disabled ? undefined : onClose}
                            sx={{
                                py: 1.5,
                                pl: 4,
                                ...(disabled ? mapTodoDisabledNavSx(theme) : {}),
                                '&.Mui-selected': {
                                    backgroundColor: 'primary.light',
                                    '&:hover': { backgroundColor: 'primary.light' },
                                },
                            }}
                        >
                            <ListItemIcon sx={{ ...listItemIconSx, mr: 1.5, minWidth: 36 }}>
                                <tab.icon />
                            </ListItemIcon>
                            <ListItemText primary={tab.label} primaryTypographyProps={{ sx: { ...listItemTextSx, fontSize: '1rem' } }} />
                        </ListItemButton>
                    );
                })}
            </>
        );
    };

    const renderInventorySubSection = () => {
        if (!pathname.startsWith('/inventory')) return null;
        return (
            <>
                {inventorySubRoutes.map(sub => {
                    const selected =
                        sub.id === 'inventory-overview'
                            ? matchInventoryItems !== null
                            : sub.id === 'inventory-categories'
                              ? matchInventoryCategories !== null
                              : sub.id === 'inventory-storage'
                                ? matchInventoryStorage !== null
                                : sub.id === 'inventory-events'
                                  ? matchInventoryEvents !== null
                                  : false;
                    return (
                        <ListItemButton
                            key={sub.id}
                            component={Link}
                            to={sub.route}
                            selected={selected}
                            onClick={onClose}
                            sx={{
                                py: 1.5,
                                pl: 4,
                                '&.Mui-selected': {
                                    backgroundColor: 'primary.light',
                                    '&:hover': { backgroundColor: 'primary.light' },
                                },
                            }}
                        >
                            <ListItemText primary={sub.label} primaryTypographyProps={{ sx: { ...listItemTextSx, fontSize: '1rem' } }} />
                        </ListItemButton>
                    );
                })}
            </>
        );
    };

    const renderTripSubSection = () => {
        if (!tripIdFromRoute) return null;
        return (
            <>
                <ListSubheader sx={{ bgcolor: 'transparent', lineHeight: 2 }}>This trip</ListSubheader>
                {tripTabsConfig.map(tab => {
                    const to = `/logistics/trip/${tripIdFromRoute}/${tab.id}`;
                    const selected = tripTabId === tab.id;
                    return (
                        <ListItemButton
                            key={tab.id}
                            component={Link}
                            to={to}
                            selected={selected}
                            onClick={onClose}
                            sx={{
                                py: 1.5,
                                pl: 4,
                                '&.Mui-selected': {
                                    backgroundColor: 'primary.light',
                                    '&:hover': { backgroundColor: 'primary.light' },
                                },
                            }}
                        >
                            <ListItemText primary={tab.label} primaryTypographyProps={{ sx: { ...listItemTextSx, fontSize: '1rem' } }} />
                        </ListItemButton>
                    );
                })}
            </>
        );
    };

    const renderOperationSubSection = () => {
        if (!operationIdFromRoute) return null;
        const baseRoute = `/logistics/operation/${operationIdFromRoute}`;
        return (
            <>
                <ListSubheader sx={{ bgcolor: 'transparent', lineHeight: 2 }}>This operation</ListSubheader>
                {operationSubRoutes.map(sub => {
                    const to = `${baseRoute}${sub.route}`;
                    const selected =
                        sub.id === 'operation-tasks'
                            ? matchLogisticsOperationTasks !== null
                            : sub.id === 'operation-confirmation'
                              ? matchLogisticsOperationConfirmation !== null
                              : sub.id === 'operation-supply'
                                ? matchLogisticsOperationSupply !== null
                                : false;
                    return (
                        <ListItemButton
                            key={sub.id}
                            component={Link}
                            to={to}
                            selected={selected}
                            onClick={onClose}
                            sx={{
                                py: 1.5,
                                pl: 4,
                                '&.Mui-selected': {
                                    backgroundColor: 'primary.light',
                                    '&:hover': { backgroundColor: 'primary.light' },
                                },
                            }}
                        >
                            <ListItemText primary={sub.label} primaryTypographyProps={{ sx: { ...listItemTextSx, fontSize: '1rem' } }} />
                        </ListItemButton>
                    );
                })}
            </>
        );
    };

    const renderLogisticsSubSection = () => {
        if (!pathname.startsWith('/logistics')) return null;
        if (tripIdFromRoute) return renderTripSubSection();
        if (operationIdFromRoute) return renderOperationSubSection();
        return (
            <>
                {logisticsSubRoutes.map(sub => {
                    const selected =
                        sub.id === 'logistics-operations'
                            ? matchLogisticsOperations !== null
                            : sub.id === 'logistics-trips'
                              ? matchLogisticsTrips !== null
                              : false;
                    return (
                        <ListItemButton
                            key={sub.id}
                            component={Link}
                            to={sub.route}
                            selected={selected}
                            onClick={onClose}
                            sx={{
                                py: 1.5,
                                pl: 4,
                                '&.Mui-selected': {
                                    backgroundColor: 'primary.light',
                                    '&:hover': { backgroundColor: 'primary.light' },
                                },
                            }}
                        >
                            <ListItemText primary={sub.label} primaryTypographyProps={{ sx: { ...listItemTextSx, fontSize: '1rem' } }} />
                        </ListItemButton>
                    );
                })}
            </>
        );
    };

    if (isLoading) {
        return (
            <Drawer
                anchor="left"
                open={open}
                onClose={onClose}
                transitionDuration={75}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: DRAWER_WIDTH,
                        boxSizing: 'border-box',
                    },
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress size={40} />
                </Box>
            </Drawer>
        );
    }

    return (
        <Drawer
            anchor="left"
            open={open}
            onClose={onClose}
            transitionDuration={75}
            sx={{
                '& .MuiDrawer-paper': {
                    width: DRAWER_WIDTH,
                    boxSizing: 'border-box',
                },
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </Box>
            <Divider />
            <List sx={{ pt: 0 }}>
                {viewOnlyWithSecret
                    ? renderEventSubSection(true)
                    : filteredGlobalTabs.map(tab => {
                          const isList = tab.id === 'list';
                          const isInventory = tab.id === 'inventory';
                          const isLogistics = tab.id === 'logistics';
                          return (
                              <Box key={tab.id}>
                                  {renderGlobalItem(tab)}
                                  {isList && renderEventSubSection(false)}
                                  {isInventory && renderInventorySubSection()}
                                  {isLogistics && renderLogisticsSubSection()}
                              </Box>
                          );
                      })}
            </List>
        </Drawer>
    );
}
